import {
  Alert,
  App,
  Button,
  DatePicker,
  Empty,
  Input,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme as antdTheme,
} from 'antd';
import type { TableProps } from 'antd';
import {
  CheckCircleOutlined,
  CopyOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  attendanceApi,
  type AttendanceStatus,
  type DailySheet,
  type HalfDayType,
  type SheetRow,
} from '@/api/attendance';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';
import { ExportButton } from '@/shared/components/ui';

const STATUS_LABEL: Record<AttendanceStatus, string> = {
  PRESENT: 'Present',
  ABSENT: 'Absent',
  LATE: 'Late',
  HALF_DAY: 'Half day',
  LEAVE: 'Leave',
};
const STATUS_COLOR: Record<AttendanceStatus, string> = {
  PRESENT: 'success',
  ABSENT: 'error',
  LATE: 'warning',
  HALF_DAY: 'processing',
  LEAVE: 'default',
};

interface RowState extends SheetRow {
  status: AttendanceStatus | null;
  halfDayType: HalfDayType | null;
  reason: string;
}

export function DailyAttendancePage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const yearId = useAuthStore((s) => s.academicYearId);
  const canSave = useAuthStore((s) => s.hasAuthority('ATTENDANCE:CREATE'));

  const [classSectionId, setClassSectionId] = useState<number | undefined>();
  const [date, setDate] = useState<Dayjs>(dayjs());
  const dateStr = date.format('YYYY-MM-DD');
  const [rows, setRows] = useState<RowState[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data: classSections } = useQuery({
    queryKey: ['setup', 'class-sections', yearId ?? 'none'],
    queryFn: () => setupApi.listClassSections(yearId ?? undefined),
    enabled: yearId != null,
    staleTime: 5 * 60 * 1000,
  });

  const sheetQuery = useQuery({
    queryKey: ['attendance', 'sheet', classSectionId, dateStr],
    queryFn: () => attendanceApi.dailySheet(classSectionId!, dateStr),
    enabled: classSectionId != null,
  });

  useEffect(() => {
    if (sheetQuery.data) {
      setRows(
        sheetQuery.data.rows.map((r) => ({
          ...r,
          status: r.mark?.status ?? null,
          halfDayType: r.mark?.halfDayType ?? null,
          reason: r.mark?.reason ?? '',
        })),
      );
      setDirty(false);
    }
  }, [sheetQuery.data]);

  const submit = useMutation({
    mutationFn: () =>
      attendanceApi.submitSheet({
        classSectionId: classSectionId!,
        date: dateStr,
        rows: rows
          .filter((r) => r.status != null)
          .map((r) => ({
            enrollmentId: r.enrollmentId,
            status: r.status!,
            halfDayType: r.status === 'HALF_DAY' ? r.halfDayType : null,
            reason: r.reason || null,
          })),
      }),
    onSuccess: (res) => {
      message.success(`Saved ${res.saved} mark${res.saved === 1 ? '' : 's'}`);
      qc.invalidateQueries({ queryKey: ['attendance'] });
      qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
      setDirty(false);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const setRow = (enrollmentId: number, patch: Partial<RowState>) => {
    setRows((rs) =>
      rs.map((r) => (r.enrollmentId === enrollmentId ? { ...r, ...patch } : r)),
    );
    setDirty(true);
  };

  const markAll = (status: AttendanceStatus) => {
    setRows((rs) =>
      rs.map((r) => ({
        ...r,
        status,
        halfDayType: status === 'HALF_DAY' ? r.halfDayType ?? 'AFTERNOON' : null,
        reason: status === 'PRESENT' || status === 'ABSENT' || status === 'LATE' ? '' : r.reason,
      })),
    );
    setDirty(true);
  };

  const { modal } = App.useApp();
  const [copyingYesterday, setCopyingYesterday] = useState(false);

  /**
   * Copies yesterday's status + half-day + reason onto each enrolled student
   * in today's sheet. New enrollments (joined since yesterday) keep whatever
   * they had today. Confirms first if there are any existing marks today —
   * better to ask than to clobber.
   */
  const applyYesterday = async () => {
    if (classSectionId == null) return;
    const yest = date.subtract(1, 'day').format('YYYY-MM-DD');
    setCopyingYesterday(true);
    try {
      const ySheet = await attendanceApi.dailySheet(classSectionId, yest);
      const byEnrollment = new Map<number, SheetRow>();
      for (const r of ySheet.rows) byEnrollment.set(r.enrollmentId, r);

      const hits = rows.reduce(
        (n, r) => (byEnrollment.get(r.enrollmentId)?.mark != null ? n + 1 : n),
        0,
      );
      if (hits === 0) {
        message.info(`No marks recorded yesterday (${dayjs(yest).format('D MMM')}) for this class.`);
        return;
      }

      const overwriting = rows.some((r) => r.status != null);
      if (overwriting) {
        const ok = await new Promise<boolean>((resolve) => {
          modal.confirm({
            title: 'Overwrite today\'s marks?',
            content: `Today already has some marks. Copying yesterday (${dayjs(yest).format('D MMM')}) will replace ${hits} matching row${hits === 1 ? '' : 's'}.`,
            okText: 'Overwrite',
            cancelText: 'Cancel',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) return;
      }

      setRows((rs) =>
        rs.map((r) => {
          const y = byEnrollment.get(r.enrollmentId);
          if (!y?.mark) return r;
          return {
            ...r,
            status: y.mark.status,
            halfDayType: y.mark.halfDayType ?? null,
            reason: y.mark.reason ?? '',
          };
        }),
      );
      setDirty(true);
      message.success(`Copied ${hits} mark${hits === 1 ? '' : 's'} from ${dayjs(yest).format('D MMM')}.`);
    } catch (e) {
      message.error(extractError(e));
    } finally {
      setCopyingYesterday(false);
    }
  };

  const sheet: DailySheet | undefined = sheetQuery.data;

  const columns: TableProps<RowState>['columns'] = [
    { title: 'Reg #', dataIndex: 'registrationNumber', width: 110, render: (rn) => <Typography.Text code>{rn}</Typography.Text> },
    {
      title: 'Name',
      render: (_, row) => (
        <Typography.Text strong>{row.firstName} {row.lastName}</Typography.Text>
      ),
    },
    {
      title: 'Status',
      width: 340,
      render: (_, row) => (
        <StatusPicker
          value={row.status}
          disabled={!canSave}
          onChange={(v) => setRow(row.enrollmentId, {
            status: v,
            halfDayType: v === 'HALF_DAY' ? row.halfDayType ?? 'AFTERNOON' : null,
          })}
        />
      ),
    },
    {
      title: 'Half-day',
      width: 150,
      render: (_, row) =>
        row.status === 'HALF_DAY' ? (
          <Select
            value={row.halfDayType ?? undefined}
            placeholder="Type"
            onChange={(v) => setRow(row.enrollmentId, { halfDayType: v })}
            options={[
              { value: 'MORNING', label: 'Morning (left early)' },
              { value: 'AFTERNOON', label: 'Afternoon (arrived late)' },
            ]}
            style={{ width: '100%' }}
            disabled={!canSave}
          />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Reason',
      render: (_, row) =>
        row.status === 'LEAVE' || row.status === 'HALF_DAY' ? (
          <Input
            placeholder="Required"
            value={row.reason}
            onChange={(e) => setRow(row.enrollmentId, { reason: e.target.value })}
            disabled={!canSave}
          />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ];

  const incomplete = rows.some(
    (r) =>
      r.status === 'HALF_DAY' && (r.halfDayType == null || !r.reason.trim())
  ) || rows.some((r) => r.status === 'LEAVE' && !r.reason.trim());

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Attendance"
        title="Daily marking"
        description="Pick a class·section and date. Re-opening a sheet shows existing marks; saving upserts changes."
        count={
          sheet ? { value: rows.filter((r) => r.status != null).length, label: `of ${rows.length} marked` } : undefined
        }
        onRefresh={() => sheetQuery.refetch()}
        refreshKey={['attendance', 'sheet', classSectionId, dateStr]}
        updatedAt={sheetQuery.dataUpdatedAt}
        actions={
          <Space>
            <ExportButton
              items={[{
                kind: 'xlsx',
                label: 'Excel',
                url: '/reports/exports/student-attendance-daily.xlsx',
                params: classSectionId != null ? { classSectionId, date: dateStr } : {},
                fileName: `student-attendance-${dateStr}.xlsx`,
                disabled: classSectionId == null,
              }]}
            />
            {canSave && (
              <Button
                type="primary"
                icon={<SaveOutlined />}
                disabled={!dirty || incomplete || classSectionId == null}
                loading={submit.isPending}
                onClick={() => submit.mutate()}
              >
                Save
              </Button>
            )}
          </Space>
        }
      />

      <Space wrap size={12} style={{ rowGap: 12 }}>
        <Select
          placeholder="Class · Section"
          value={classSectionId}
          onChange={setClassSectionId}
          options={(classSections ?? [])
            .slice()
            .sort((a, b) => a.classSortOrder - b.classSortOrder || a.sectionName.localeCompare(b.sectionName))
            .map((cs) => ({
              value: cs.id,
              label: `${cs.className} · ${cs.sectionName}`,
            }))}
          style={{ minWidth: 240 }}
          showSearch
          optionFilterProp="label"
        />
        <DatePicker
          value={date}
          onChange={(d) => d && setDate(d)}
          format="DD MMM YYYY"
          allowClear={false}
          disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
        />
        <div style={{ flex: 1 }} />
        {sheet && rows.length > 0 && (
          <Space size="small">
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Quick mark all:</Typography.Text>
            <Button size="small" icon={<CheckCircleOutlined />} onClick={() => markAll('PRESENT')}>Present</Button>
            <Button size="small" onClick={() => markAll('ABSENT')}>Absent</Button>
            {canSave && (
              <Tooltip title={`Copy each student's mark from ${date.subtract(1, 'day').format('D MMM')}`}>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  loading={copyingYesterday}
                  onClick={() => applyYesterday()}
                >
                  Use yesterday
                </Button>
              </Tooltip>
            )}
          </Space>
        )}
      </Space>

      {sheet && sheet.mode !== 'WORKING' && (
        <Alert
          type={sheet.mode === 'HOLIDAY' ? 'warning' : 'info'}
          showIcon
          message={
            sheet.mode === 'HOLIDAY'
              ? `Holiday: ${sheet.offReason ?? 'school holiday'}`
              : `${sheet.offReason ?? 'Weekly off'}`
          }
          description="Marking is allowed but the day is normally excluded from percentage calculations."
        />
      )}

      {classSectionId == null ? (
        <Empty description="Pick a class · section to start" />
      ) : sheet && rows.length === 0 ? (
        <Empty description="No active enrollments for this class · section" />
      ) : (
        <Table
          rowKey="enrollmentId"
          size="middle"
          loading={sheetQuery.isFetching}
          columns={columns}
          dataSource={rows}
          pagination={false}
          summary={() =>
            rows.length === 0 ? null : (
              <Table.Summary fixed="bottom">
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <Typography.Text strong>Counts</Typography.Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} colSpan={3}>
                    <Space wrap>
                      {(['PRESENT', 'ABSENT', 'LATE', 'HALF_DAY', 'LEAVE'] as AttendanceStatus[]).map((s) => {
                        const n = rows.filter((r) => r.status === s).length;
                        return (
                          <Tag key={s} color={STATUS_COLOR[s]}>
                            {STATUS_LABEL[s]}: {n}
                          </Tag>
                        );
                      })}
                      <Tag>
                        Unmarked: {rows.filter((r) => r.status == null).length}
                      </Tag>
                    </Space>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )
          }
        />
      )}
    </div>
  );
}

const STATUS_ORDER: AttendanceStatus[] = ['PRESENT', 'ABSENT', 'LATE', 'HALF_DAY', 'LEAVE'];
const STATUS_SHORT: Record<AttendanceStatus, string> = {
  PRESENT: 'P',
  ABSENT: 'A',
  LATE: 'L',
  HALF_DAY: '½',
  LEAVE: 'Lv',
};

/**
 * Per-row inline status picker — replaces the old Select dropdown with a
 * 5-segment touch target. One tap to mark; same height as a button so the
 * teacher can scan the column visually instead of opening dropdowns.
 *
 * The Segmented variant carries semantic color through the active option
 * via a CSS-var that the active-state selector picks up. Falls back to
 * AntD's default tinted active state when the var isn't set.
 *
 * Tooltips on each option spell out the full status name so cashier/teacher
 * users don't have to memorize the one-letter labels — useful in the first
 * week, harmless thereafter.
 */
function StatusPicker({
  value,
  onChange,
  disabled,
}: {
  value: AttendanceStatus | null;
  onChange: (next: AttendanceStatus) => void;
  disabled?: boolean;
}) {
  const { token } = antdTheme.useToken();
  const activeColor: Record<AttendanceStatus, string> = {
    PRESENT: token.colorSuccess,
    ABSENT: token.colorError,
    LATE: token.colorWarning,
    HALF_DAY: token.colorInfo,
    LEAVE: token.colorTextTertiary,
  };
  return (
    <Segmented<AttendanceStatus>
      block
      value={value ?? undefined}
      onChange={(v) => onChange(v as AttendanceStatus)}
      disabled={disabled}
      style={value ? ({ ['--status-tint' as string]: activeColor[value] }) : undefined}
      options={STATUS_ORDER.map((s) => ({
        value: s,
        label: (
          <Tooltip title={STATUS_LABEL[s]} mouseEnterDelay={0.4}>
            <span style={{
              fontSize: 12,
              fontWeight: value === s ? 700 : 500,
              color: value === s ? activeColor[s] : undefined,
              display: 'inline-block',
              minWidth: 22,
              textAlign: 'center',
            }}>
              {STATUS_SHORT[s]}
            </span>
          </Tooltip>
        ),
      }))}
    />
  );
}
